import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Page header (h44, 01-design-system §6.4): title + right actions. The bar belongs to the shell; pages fill it by
 * rendering <PageHeader title=… actions=… /> anywhere in their tree — the content is portalled into the bar, so it
 * keeps the page's React context (state, queries, forms).
 */
interface HeaderSlots {
  title: HTMLElement | null;
  actions: HTMLElement | null;
  setTitle(el: HTMLElement | null): void;
  setActions(el: HTMLElement | null): void;
  /** number of mounted <PageHeader> instances; the bar shows its default title while there are none */
  claims: number;
  claim(): () => void;
}

const HeaderContext = createContext<HeaderSlots | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<HTMLElement | null>(null);
  const [actions, setActions] = useState<HTMLElement | null>(null);
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  }, []);
  return <HeaderContext value={{ title, actions, setTitle, setActions, claims, claim }}>{children}</HeaderContext>;
}

/**
 * The bar itself (rendered by AppShell). `fallbackTitle` is shown when the page renders no <PageHeader>; `trailing`
 * is shell content after the page's actions (the alarms bell).
 */
export function PageHeaderBar({ fallbackTitle, trailing }: { fallbackTitle?: ReactNode; trailing?: ReactNode }) {
  const ctx = useContext(HeaderContext);
  return (
    <header
      // borders are drawn *outside* in Figma (497:21106 sa=OUTSIDE): the top one belongs to the tab strip, the bottom
      // one hangs 1 px into the gutter below
      className="relative z-10 flex h-44 shrink-0 items-center gap-10 bg-panel px-14 py-10 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-px after:bg-line-subtle"
    >
      <div ref={ctx?.setTitle} className="flex min-w-0 flex-1 items-center gap-10">
        {!ctx?.claims && fallbackTitle ? <h1 className="truncate t-page-title text-fg">{fallbackTitle}</h1> : null}
      </div>
      <div ref={ctx?.setActions} className="flex shrink-0 items-center gap-10 empty:hidden" />
      {trailing}
    </header>
  );
}

export interface PageHeaderProps {
  /** page title (`t-page-title`, 600 16/24) — a string, or any node (e.g. title + badge) */
  title?: ReactNode;
  /** right-aligned controls, `sm` size (h24), gap 10 — search, segmented, buttons */
  actions?: ReactNode;
}

/**
 * Sets the shell's page header while mounted:
 *
 *   <PageHeader title="Barcha xodimlar" actions={<><SearchButton /><Button size="sm">Xodim qo'shish</Button></>} />
 */
export function PageHeader({ title, actions }: PageHeaderProps) {
  const { t } = useTranslation();
  const ctx = useContext(HeaderContext);
  const claim = ctx?.claim;
  useLayoutEffect(() => claim?.(), [claim]);
  const appName = t('app.name');
  useEffect(() => {
    if (typeof title === 'string' && title) document.title = `${title} · ${appName}`;
  }, [title, appName]);
  if (!ctx) return null;
  return (
    <>
      {ctx.title && title != null
        ? createPortal(typeof title === 'string' ? <h1 className="truncate t-page-title text-fg">{title}</h1> : title, ctx.title)
        : null}
      {ctx.actions && actions != null ? createPortal(actions, ctx.actions) : null}
    </>
  );
}
